import React from 'react'
import styled from 'styled-components'
import { Label, Value, Display } from '../Attribute'
import Tooltip from 'components/Tooltip'
import Color from '../Attribute/components/Color'
import ColorVariableAttribute from './ColorVariableAttribute'
import ColorDropdown from '../ColorDropdown'
import rgbTo, { ColorFormat } from '../Attribute/components/Color/utils'
import { Color as ColorType, ColorVariable } from 'modules/shares/types'
import copy from 'copy-to-clipboard'
import useHandleCopyValue from 'utils/hooks/useHandleCopyValue'
import DirtyIconTooltip from 'modules/shares/Inspector/Sidebar/components/Style/DirtyIconTooltip'

const copyFormat = (format: ColorFormat, colorParams: ColorType) => {
  copy(rgbTo(format, colorParams))
}

interface ColorAttributeProps extends ColorType {
  label?: string
  style?: React.CSSProperties
  colorVariables?: { [key: string]: ColorVariable }
  display?: Display
  fullWidthColor?: boolean
  dirtyAttributes?: { originalValue: ColorType; originalProperty: string }
}

export const ClickableArea = styled(Label)`
  cursor: pointer;
  width: 100%;
  display: flex;
`

export const LabelWithWarning = styled(Label)`
  display: flex;
  width: 33%;
  align-content: center;
`

const FlexValue = styled(Value)`
  display: flex;
`

// 10px is the size of the dropdown icon
const ColorWithDropdown = styled(Color)`
  max-width: calc(100% - 10px);
`

const ColorAttribute: React.FC<
  ColorAttributeProps & {
    onColorFormatChange: (f: ColorFormat) => void
    colorFormat: ColorFormat
  }
> = props => {
  const {
    red,
    green,
    blue,
    alpha = 1,
    colorVariableID = undefined,
    colorVariables = undefined,
    label = 'Color',
    onColorFormatChange,
    colorFormat,
    dirtyAttributes,
  } = props
  const colorParams = { red, green, blue, alpha }
  const copyValue = rgbTo(colorFormat, colorParams)

  const {
    handleCopyValue,
    copyTooltipText,
    handleEnterCopiableArea,
    handleLeaveCopiableArea,
    copyTooltipVisible,
  } = useHandleCopyValue(copyValue)

  if (colorVariables && colorVariableID) {
    return <ColorVariableAttribute {...props} />
  }

  const dirtyColorValues = []

  if (dirtyAttributes) {
    dirtyColorValues.push(rgbTo(colorFormat, dirtyAttributes.originalValue))

    if (
      dirtyAttributes.originalValue?.alpha &&
      colorFormat === ColorFormat.HEX
    ) {
      dirtyColorValues.push(
        `opacity ${dirtyAttributes.originalValue?.alpha * 100}%`
      )
    }
  }

  return (
    <Tooltip
      placement="left"
      spacing="10px"
      visible={copyTooltipVisible}
      content={copyTooltipText}>
      <ClickableArea
        onClick={handleCopyValue}
        onMouseEnter={handleEnterCopiableArea}
        onMouseLeave={handleLeaveCopiableArea}>
        {label && (
          <LabelWithWarning>
            {/* the extra handleEnterCopiableArea is to make sure the copy tooltip is triggered after leaving the dirty icon tooltip */}
            <div
              aria-hidden
              onMouseEnter={
                dirtyAttributes ? handleEnterCopiableArea : undefined
              }>
              {label}
            </div>
            {dirtyAttributes && (
              <DirtyIconTooltip
                originalProperty={dirtyAttributes.originalProperty}
                originalValue={dirtyColorValues.join(', ')}
                preventLabelTooltip={handleLeaveCopiableArea}
              />
            )}
          </LabelWithWarning>
        )}
        <FlexValue
          valueString={copyValue}
          label="Color"
          onMouseEnter={dirtyAttributes ? handleEnterCopiableArea : undefined}>
          <ColorWithDropdown
            red={red}
            green={green}
            blue={blue}
            alpha={alpha}
            format={colorFormat}
          />
          <ColorDropdown
            onChange={newFormat => {
              copyFormat(newFormat, colorParams)
              onColorFormatChange(newFormat)
            }}
          />
        </FlexValue>
      </ClickableArea>
    </Tooltip>
  )
}

export default ColorAttribute
